import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CardContent, Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButtonGroup, ToggleButton, TextField, CircularProgress } from '@mui/material';
import { PiDownloadSimple, PiEye, PiCloudArrowUp, PiCode, PiList, PiChatCircle } from 'react-icons/pi';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { apiService } from '../services/apiService';
import { schemaService, WorkflowUiSchema } from '../services/schemaService';
import FilePreviewModal from './FilePreviewModal';
import WorkflowReviewForm from './WorkflowReviewForm';
import { SaveToIManageButton } from './iManage/SaveToIManageButton';
import { useAuth } from '../auth/AuthProvider';
import {
  filterOutputFiles,
  getDownloadFilename,
  isMultiStepWorkflow,
  requiresReview,
  getCompletionActions,
  getReviewFormConfig,
  getStepControls,
  getInputDisplayConfig,
  shouldShowOriginalDocuments,
} from '../utils/workflowSchemaHelpers';

// PDF Viewer component that handles signed URL fetching
function PDFViewer({ status }: { status: any }) {
  const [signedUrls, setSignedUrls] = useState<Map<string, string>>(new Map());
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewableFiles, setViewableFiles] = useState<{ uri: string; label: string }[]>([]);

  React.useEffect(() => {
    const fetchFiles = async () => {
      // V1: flat string array
      const inputFiles = status?.input?.input_file_s3_uris || [];
      // V2: object array with {s3_uri, identifier}
      const v2Raw = status?.input?.workflow_data?.input_files || status?.input?.workflow_data?.files || [];
      const v2InputFiles = v2Raw.map((f: any) => typeof f === 'string' ? { uri: f } : { uri: f?.s3_uri, identifier: f?.identifier });
      const attachments = status?.input?.workflow_data?.attachments || [];

      // Build deduplicated list with labels
      const files: { uri: string; label: string }[] = [];
      const seen = new Set<string>();

      for (const uri of inputFiles) {
        if (uri && !seen.has(uri)) {
          seen.add(uri);
          files.push({ uri, label: uri.split('/').pop() || 'Document' });
        }
      }
      for (const f of v2InputFiles) {
        if (f.uri && !seen.has(f.uri)) {
          seen.add(f.uri);
          const label = f.identifier
            ? f.identifier.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
            : f.uri.split('/').pop() || 'Document';
          files.push({ uri: f.uri, label });
        }
      }
      for (const uri of attachments) {
        if (uri && !seen.has(uri)) {
          seen.add(uri);
          files.push({ uri, label: uri.split('/').pop() || 'Attachment' });
        }
      }

      if (files.length === 0) {
        setError('No input documents found');
        return;
      }

      setViewableFiles(files);
      setActiveFileIndex(0);

      // Fetch signed URL for the first file
      setLoading(true);
      try {
        const response = await apiService.getSignedViewUrl!(files[0].uri);
        setSignedUrls(new Map([[files[0].uri, response.signedUrl]]));
      } catch (err: any) {
        console.error('Failed to get signed URL:', err);
        // Try attachment fallback for first file
        if (attachments.length > 0 && inputFiles.includes(files[0].uri)) {
          const fallback = attachments.find((uri: string) => uri.toLowerCase().includes('.pdf'));
          if (fallback) {
            try {
              const retryResponse = await apiService.getSignedViewUrl!(fallback);
              setSignedUrls(new Map([[files[0].uri, retryResponse.signedUrl]]));
              setLoading(false);
              return;
            } catch (_) { /* fall through */ }
          }
        }
        if (err.response?.status === 404 || err.message?.includes('File not found')) {
          setError('Document not found in storage');
        } else {
          setError('Failed to load document');
        }
      } finally {
        setLoading(false);
      }
    };

    if (status) {
      fetchFiles();
    }
  }, [status]);

  const handleFileSwitch = async (index: number) => {
    const file = viewableFiles[index];
    if (!file) return;
    setActiveFileIndex(index);

    if (signedUrls.has(file.uri)) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getSignedViewUrl!(file.uri);
      setSignedUrls(prev => new Map(prev).set(file.uri, response.signedUrl));
    } catch (err: any) {
      console.error('Failed to get signed URL:', err);
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const activeUrl = viewableFiles[activeFileIndex] ? signedUrls.get(viewableFiles[activeFileIndex].uri) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {viewableFiles.length > 1 && (
        <Box sx={{ display: 'flex', gap: 0.5, p: 1, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
          {viewableFiles.map((file, i) => (
            <Button
              key={file.uri}
              size="small"
              variant={i === activeFileIndex ? 'contained' : 'outlined'}
              onClick={() => handleFileSwitch(i)}
              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, minWidth: 0 }}
            >
              {file.label}
            </Button>
          ))}
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography>Loading document...</Typography>
          </Box>
        ) : error ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {error}
          </Typography>
        ) : activeUrl ? (
          <iframe
            src={activeUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title={viewableFiles[activeFileIndex]?.label || 'Original Document'}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No document available
          </Typography>
        )}
      </Box>
    </Box>
  );
}

interface OutputFile {
  name: string;
  size: number;
  type: string;
  s3Uri?: string;
  url?: string;
  identifier?: string;
}

interface DownloadOutputsProps {
  files: OutputFile[];
  executionArn: string;
  status?: any;
}

export default function DownloadOutputs({ files, executionArn, status }: DownloadOutputsProps) {
  const [previewFile, setPreviewFile] = useState<OutputFile | null>(null);
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
  const [jsonData, setJsonData] = useState<any>(null);
  const [workflowUiSchema, setWorkflowUiSchema] = useState<WorkflowUiSchema | null>(null);
  const { user } = useAuth();
  // File input ref for JSON upload
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // View mode for review dialog: 'form' or 'json'
  const [viewMode, setViewMode] = useState<'form' | 'json'>('form');
  // Raw JSON text for direct editing
  const [jsonText, setJsonText] = useState<string>('');
  // JSON parse error message
  const [jsonError, setJsonError] = useState<string | null>(null);
  // S3 URI of the loaded output file (for saving edits back)
  const [sourceS3Uri, setSourceS3Uri] = useState<string | null>(null);
  // save-in-progress and success feedback
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const aiChatEnabled = process.env.REACT_APP_AI_CHAT_ENABLED === 'true';
  const imanageEnabled = process.env.REACT_APP_IMANAGE_ENABLED === 'true';
  
  // Fetch UI schema for this workflow
  // resolve practice/workflow from V1 or V2 input formats
  const resolvedPractice = status?.input?.practice || status?.input?.metadata?.practice || status?.input?.execution_metadata?.practice;
  const resolvedWorkflow = status?.input?.workflows?.[0] || status?.input?.metadata?.workflow || status?.input?.execution_metadata?.workflow;
  // V2 workflows have metadata object in the SF input
  const isV2Workflow = Boolean(status?.input?.metadata);

  useEffect(() => {
    const fetchUiSchema = async () => {
      if (resolvedPractice && resolvedWorkflow) {
        try {
          const response = await schemaService.getWorkflowUiSchema(resolvedPractice, resolvedWorkflow);
          if (response) {
            setWorkflowUiSchema(response.uiSchema);
          }
        } catch (error) {
          console.log('No UI schema available for workflow');
        }
      }
    };
    fetchUiSchema();
  }, [resolvedPractice, resolvedWorkflow]);

  // Schema-driven helpers (replace hardcoded isLeaseAbstraction, isOceanbankStepOne)
  const isMultiStepWithReview = () => {
    return isMultiStepWorkflow(workflowUiSchema) && requiresReview(workflowUiSchema);
  };
  
  const getDownloadFileName = (file: OutputFile) => {
    return getDownloadFilename(file, workflowUiSchema, {
      jobNumber: status?.input?.number || status?.input?.execution_metadata?.original_input?.number,
      workflow: status?.input?.workflows?.[0],
      practice: status?.input?.practice,
    });
  };
  
  // show the real download filename so the card label matches what gets downloaded
  const getDisplayLabel = (file: OutputFile) => {
    return getDownloadFileName(file);
  };
  
  const getFilteredFiles = () => {
    return filterOutputFiles(files, workflowUiSchema, { isV2: isV2Workflow });
  };
  
  const completionActions = getCompletionActions(workflowUiSchema);
  const reviewFormConfig = getReviewFormConfig(workflowUiSchema);
  const stepControls = getStepControls(workflowUiSchema);
  const inputDisplayConfig = getInputDisplayConfig(workflowUiSchema);
  const showOriginalDocs = shouldShowOriginalDocuments(workflowUiSchema);
  
  // Get JSON file from output
  const getJsonOutputFile = () => {
    const allFiles = status?.output?.output_files || [];
    return allFiles.find((file: any) => {
      const fileType = file.s3_uri ? file.s3_uri.split('.').pop()?.toLowerCase() : file.name?.split('.').pop()?.toLowerCase();
      return fileType === 'json';
    });
  };
  
  // Handler for downloading JSON data
  const handleDownloadJson = async () => {
    try {
      const jsonFile = getJsonOutputFile();
      if (!jsonFile) {
        alert('No JSON data file found.');
        return;
      }
      
      const jobNumber = status?.input?.number || 'workflow';
      const downloadName = `${jobNumber}-extracted-data.json`;
      const signedUrl = await apiService.getSignedDownloadUrl(jsonFile.s3_uri, downloadName);
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('JSON download failed:', error);
      alert('Failed to download JSON data.');
    }
  };
  
  // Handler for uploading JSON file
  const handleUploadJsonClick = () => {
    fileInputRef.current?.click();
  };

  const handleJsonFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsedData = JSON.parse(text);
      
      // Unwrap common response wrappers: { responses: [...] } or { final_response: {...} }
      // Guard: only unwrap final_response if it's an object (not a string from intermediate steps)
      const actualData = parsedData.responses?.[0]
        || (parsedData.final_response && typeof parsedData.final_response === 'object' ? parsedData.final_response : null)
        || parsedData;
      
      // Set the data and open editor for review before triggering step 2
      setJsonData(actualData);
      setJsonEditorOpen(true);
    } catch (error) {
      console.error('Failed to parse JSON file:', error);
      alert('Invalid JSON file. Please upload a valid JSON file.');
    }
    
    // Reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditData = async () => {
    try {
      // Look in ALL files from status (not filtered files)
      // Use last JSON file to prefer the final step's output (V2 files are ordered by step)
      const allFiles = status?.output?.output_files || [];
      const jsonFiles = allFiles.filter((file: any) => {
        const fileType = file.s3_uri ? file.s3_uri.split('.').pop()?.toLowerCase() : file.name?.split('.').pop()?.toLowerCase();
        return fileType === 'json';
      });
      const jsonFile = jsonFiles[jsonFiles.length - 1];
      

      
      if (!jsonFile) {
        console.error('DownloadOutputs - No JSON file found in output files');
        alert('No JSON data found to edit.');
        return;
      }
      
      // track where the file lives so we can save edits back
      setSourceS3Uri(jsonFile.s3_uri || null);
      setSaveSuccess(false);
      
      // Get the JSON content
      let content;
      if (jsonFile.url) {
        const response = await fetch(jsonFile.url);
        content = await response.text();
      } else if (jsonFile.s3_uri) {
        content = await apiService.getFileContent(jsonFile.s3_uri);
      }
      
      console.log('Raw content received:', typeof content, content.substring(0, 100));
      
      // Handle potential base64 encoding
      let decodedContent = content;
      if (typeof content === 'string' && content.match(/^[A-Za-z0-9+/]+=*$/)) {
        try {
          decodedContent = atob(content);
          console.log('Decoded base64 content:', decodedContent.substring(0, 100));
        } catch (e) {
          console.log('Content is not base64, using as-is');
          decodedContent = content;
        }
      }
      
      const jsonData = typeof decodedContent === 'string' ? JSON.parse(decodedContent) : decodedContent;
      
      // Unwrap common response wrappers: { responses: [...] } or { final_response: {...} }
      // Guard: only unwrap final_response if it's an object (not a string from intermediate steps)
      const actualData = jsonData.responses?.[0]
        || (jsonData.final_response && typeof jsonData.final_response === 'object' ? jsonData.final_response : null)
        || jsonData;
      setJsonData(actualData);
      setJsonEditorOpen(true);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load loan data.');
    }
  };

  const handleSaveReview = async () => {
    if (!sourceS3Uri || !jsonData) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      await apiService.saveFileContent(sourceS3Uri, JSON.stringify(jsonData, null, 2));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('Failed to save review data:', error);
      const detail = error?.message || 'Unknown error';
      alert(`Failed to save changes: ${detail}`);
    } finally {
      setSaving(false);
    }
  };
  
  const handleSubmitToNextStep = async () => {
    try {
      // Get next workflow from UI schema
      const nextWorkflow = workflowUiSchema?.multi_step_config?.next_workflow;
      if (!nextWorkflow) {
        console.error('No next workflow configured');
        alert('No next workflow configured.');
        return;
      }
      
      // First, save the reviewed JSON data to S3
      const jobName = status?.input?.number 
        || status?.input?.execution_metadata?.original_input?.number 
        || 'workflow';
      const saveResult = await apiService.saveJsonToS3(jsonData, jobName);
      
      // Store the parent workflow for job grouping, but execute actual step workflow
      const parentWorkflowName = status?.input?.workflows?.[0] || nextWorkflow.workflow;
      
      // Then execute next workflow with the S3 URI
      const workflowData = {
        practice: nextWorkflow.practice,
        workflows: [nextWorkflow.workflow], // Use actual step workflow for execution (e.g., "ocean-bank-step-two")
        number: `${jobName}-step-two`,
        input_file_s3_uris: [saveResult.s3Uri],
        workflow_data: {
          isStepTwo: true,
          parentWorkflow: parentWorkflowName, // For job grouping on unified page
          stepWorkflow: nextWorkflow.workflow,
        }
      };
      
      const result = await apiService.executeWorkflow(workflowData);
      setJsonEditorOpen(false);
      
      // Refresh to show the new job in the queue
      window.location.reload();
    } catch (error) {
      console.error('Failed to start next workflow step:', error);
      alert('Failed to start next workflow step.');
    }
  };
  
  const [chatLoading, setChatLoading] = useState(false);
  
  const handleChatWithFiles = async () => {
    setChatLoading(true);
    try {
      // Create a new conversation
      const response = await apiService.createChatConversation!(
        `Chat about ${status?.input?.number || 'Workflow Results'}`
      );
      
      console.log('DownloadOutputs - createChatConversation response:', response);
      const conversationId = response.conversation?.conversationId;
      
      if (!conversationId) {
        console.error('DownloadOutputs - No conversationId in response');
        alert('Failed to get conversation ID.');
        return;
      }
      
      // Prepare file attachments from output files
      // For multi-step workflows with review, include all files. Otherwise use filtered list.
      const filesToAttach = isMultiStepWithReview() ? files : getFilteredFiles();
      const fileAttachments = filesToAttach
        .filter(file => file.s3Uri) // Only include files with S3 URIs
        .map(file => ({
          fileName: getDownloadFileName(file),
          s3Uri: file.s3Uri!,
          fileType: file.type,
          fileSize: file.size
        }));
      
      console.log('DownloadOutputs - filtered files:', getFilteredFiles());
      console.log('DownloadOutputs - attaching files:', fileAttachments);
      
      if (fileAttachments.length === 0) {
        console.warn('DownloadOutputs - No files with S3 URIs found to attach');
      }
      
      // Navigate with file attachments as URL parameters
      const fileParams = fileAttachments.map((file: any) => 
        `file=${encodeURIComponent(JSON.stringify(file))}`
      ).join('&');
      
      const chatUrl = `/chat?conversation=${conversationId}${fileParams ? '&' + fileParams : ''}`;
      console.log('DownloadOutputs - navigating to:', chatUrl);
      window.location.href = chatUrl;
      
    } catch (error) {
      console.error('Failed to create chat:', error);
      alert('Failed to start chat with files.');
    } finally {
      setChatLoading(false);
    }
  };

  const handleDownload = async (file: OutputFile) => {
    try {
      const downloadName = getDownloadFileName(file);
      
      if (file.url) {
        // Use direct URL
        const link = document.createElement('a');
        link.href = file.url;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (file.s3Uri) {
        // For real data, get signed URL with custom filename and download
        const signedUrl = await apiService.getSignedDownloadUrl(file.s3Uri, downloadName);
        const link = document.createElement('a');
        link.href = signedUrl;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('No download URL or S3 URI available for file:', file);
        alert('No download URL available for this file.');
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  const [zipping, setZipping] = useState(false);

  const handleDownloadAll = async () => {
    const filesToDownload = isMultiStepWithReview() ? files : getFilteredFiles();
    if (filesToDownload.length === 0) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      for (const file of filesToDownload) {
        if (!file.s3Uri) continue;
        try {
          const downloadName = getDownloadFileName(file);
          const signedUrl = await apiService.getSignedDownloadUrl(file.s3Uri, downloadName);
          const blob = await fetch(signedUrl).then(r => r.blob());
          zip.file(downloadName, blob);
        } catch (err) {
          console.warn('failed to fetch file for zip:', file.s3Uri, err);
        }
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const jobNumber = status?.input?.number || status?.input?.execution_metadata?.original_input?.number || 'output';
      saveAs(content, `${jobNumber}-files.zip`);
    } catch (err) {
      console.error('zip download failed:', err);
      alert('Failed to create ZIP. Please try again.');
    } finally {
      setZipping(false);
    }
  };

  const getFileTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'docx': return 'primary';
      case 'pdf': return 'error';
      case 'json': return 'success';
      case 'xlsx': return 'warning';
      default: return 'default';
    }
  };

  // Check if file type supports preview (exclude Microsoft Office formats)
  const supportsPreview = (type: string) => {
    const microsoftFormats = ['docx', 'xlsx', 'pptx', 'doc', 'xls', 'ppt'];
    return !microsoftFormats.includes(type.toLowerCase());
  };

  return (
    <Box>
      {/* Debug info for multi-step workflows with review */}
      {isMultiStepWithReview() && process.env.NODE_ENV === 'development' && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" display="block">
            DownloadOutputs Debug Info:
          </Typography>
          <Typography variant="caption" display="block">
            All files from status: {JSON.stringify(status?.output?.output_files?.map((f: any) => ({ name: f.name, s3_uri: f.s3_uri })) || [])}
          </Typography>
          <Typography variant="caption" display="block">
            Processed files prop: {JSON.stringify(files.map(f => ({ name: f.name, s3Uri: f.s3Uri, type: f.type })))}
          </Typography>
        </Box>
      )}

      {/* Hidden file input for JSON upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".json,application/json"
        onChange={handleJsonFileSelected}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Output Files ({isMultiStepWithReview() ? files.length : getFilteredFiles().length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {aiChatEnabled && (isMultiStepWithReview() ? files.length > 0 : getFilteredFiles().length > 0) && (
            <Button
              variant="outlined"
              startIcon={<PiChatCircle size={18} />}
              onClick={handleChatWithFiles}
              disabled={chatLoading}
              size="small"
            >
              {chatLoading ? 'Starting Chat...' : 'Chat with Files'}
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={zipping ? <CircularProgress size={16} color="inherit" /> : <PiDownloadSimple size={18} />}
            onClick={handleDownloadAll}
            disabled={zipping || (isMultiStepWithReview() ? files.length === 0 : getFilteredFiles().length === 0)}
          >
            {zipping ? 'Zipping...' : 'Download All'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {isMultiStepWithReview() ? (
          // Schema-driven UI for multi-step workflows with review
          <>
            {/* Multi-step workflow controls card */}
            <Grid item xs={12}>
              <Card sx={{ mb: 2, border: '2px solid', borderColor: 'primary.main' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {reviewFormConfig?.title || completionActions.completionMessage || 'Step Complete'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {reviewFormConfig?.description || 'Review and complete the extracted data to continue'}
                  </Typography>
                  
                  {/* Main action button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleEditData}
                      sx={{ minWidth: 200 }}
                    >
                      {completionActions.reviewButtonText || 'Review & Continue'}
                    </Button>
                  </Box>
                  
                  {/* Secondary actions row - schema-driven */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* Download JSON Button */}
                    {stepControls.allowIntermediateDownload && getJsonOutputFile() && (
                      <Button
                        variant="outlined"
                        startIcon={<PiDownloadSimple size={18} />}
                        onClick={handleDownloadJson}
                        size="small"
                      >
                        {stepControls.downloadButtonText}
                      </Button>
                    )}
                    
                    {/* Upload JSON Button */}
                    {stepControls.allowManualTrigger && (
                      <Button
                        variant="outlined"
                        startIcon={<PiCloudArrowUp size={18} />}
                        onClick={handleUploadJsonClick}
                        size="small"
                      >
                        Upload JSON
                      </Button>
                    )}
                    
                    {/* Skip Review Button */}
                    {stepControls.allowManualTrigger && getJsonOutputFile() && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleSubmitToNextStep}
                        size="small"
                      >
                        Skip Review & Continue
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Show iManage Save button */}
            {imanageEnabled && user && files.length > 0 && (
              <Grid item xs={12}>
                <Card sx={{ mb: 2, border: '2px solid', borderColor: 'success.main' }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Save to iManage
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Save processed documents to your iManage workspace
                    </Typography>
                    <SaveToIManageButton
                      jobId={status?.input?.number || executionArn}
                      outputFiles={files.filter(f => f.s3Uri).map(f => f.s3Uri!)}
                      jobMetadata={{
                        practice: status?.input?.practice || 'unknown',
                        workflow: status?.input?.workflows?.join(', ') || 'unknown',
                        clientMatter: status?.input?.clientMatter
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
            
            {/* Show all files for preview/download */}
            {files.map((file, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ wordBreak: 'break-word', flex: 1 }}>
                        {getDisplayLabel(file)}
                      </Typography>
                      <Chip 
                        label={file.type.toUpperCase()} 
                        size="small" 
                        color={getFileTypeColor(file.type)}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {(file.size / 1024).toFixed(1)} KB
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      {supportsPreview(file.type) && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PiEye size={18} />}
                          onClick={() => setPreviewFile(file)}
                          sx={{ flex: 1 }}
                        >
                          Preview
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<PiDownloadSimple size={18} />}
                        onClick={() => handleDownload(file)}
                        sx={{ flex: supportsPreview(file.type) ? 1 : 'auto', width: supportsPreview(file.type) ? 'auto' : '100%' }}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </>
        ) : (
          // Regular file display for other workflows
          <>
            {/* Show iManage Save button for non-Ocean Bank workflows */}
            {imanageEnabled && user && getFilteredFiles().length > 0 && (
              <Grid item xs={12}>
                <Card sx={{ mb: 2, border: '2px solid', borderColor: 'success.main' }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Save to iManage
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Save processed documents to your iManage workspace
                    </Typography>
                    <SaveToIManageButton
                      jobId={status?.input?.number || executionArn}
                      outputFiles={getFilteredFiles().filter(f => f.s3Uri).map(f => f.s3Uri!)}
                      jobMetadata={{
                        practice: status?.input?.practice || 'unknown',
                        workflow: status?.input?.workflows?.join(', ') || 'unknown',
                        clientMatter: status?.input?.clientMatter
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
            
            {getFilteredFiles().map((file, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ wordBreak: 'break-word', flex: 1 }}>
                        {getDisplayLabel(file)}
                      </Typography>
                      <Chip 
                        label={file.type.toUpperCase()} 
                        size="small" 
                        color={getFileTypeColor(file.type)}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {(file.size / 1024).toFixed(1)} KB
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      {supportsPreview(file.type) && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PiEye size={18} />}
                          onClick={() => setPreviewFile(file)}
                          sx={{ flex: 1 }}
                        >
                          Preview
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<PiDownloadSimple size={18} />}
                        onClick={() => handleDownload(file)}
                        sx={{ flex: supportsPreview(file.type) ? 1 : 'auto', width: supportsPreview(file.type) ? 'auto' : '100%' }}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </>
        )}
      </Grid>

      {!isMultiStepWithReview() && getFilteredFiles().length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No output files available
        </Typography>
      )}
      
      {isMultiStepWithReview() && files.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No output files available
        </Typography>
      )}
      
      <FilePreviewModal
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
      />
      
      <Dialog
        open={jsonEditorOpen}
        onClose={() => setJsonEditorOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '95vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{reviewFormConfig?.title || 'Review Information'}</span>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => {
              if (newMode !== null) {
                if (newMode === 'json') {
                  // Switching to JSON view - serialize current data
                  setJsonText(JSON.stringify(jsonData, null, 2));
                  setJsonError(null);
                } else {
                  // Switching to form view - try to parse JSON text
                  try {
                    const parsed = JSON.parse(jsonText);
                    setJsonData(parsed);
                    setJsonError(null);
                  } catch (e) {
                    setJsonError('Invalid JSON - fix errors before switching to form view');
                    return; // Don't switch if JSON is invalid
                  }
                }
                setViewMode(newMode);
              }
            }}
            size="small"
          >
            <ToggleButton value="form" aria-label="form view">
              <PiList size={18} style={{ marginRight: 4 }} />
              Form
            </ToggleButton>
            <ToggleButton value="json" aria-label="json view">
              <PiCode size={18} style={{ marginRight: 4 }} />
              JSON
            </ToggleButton>
          </ToggleButtonGroup>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', height: '100%' }}>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {viewMode === 'form' ? (
              <WorkflowReviewForm
                initialData={jsonData}
                onDataChange={setJsonData}
                uiSchema={workflowUiSchema}
              />
            ) : (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {jsonError && (
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {jsonError}
                  </Typography>
                )}
                <TextField
                  multiline
                  fullWidth
                  value={jsonText}
                  onChange={(e) => {
                    setJsonText(e.target.value);
                    // Try to parse and update jsonData in real-time
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setJsonData(parsed);
                      setJsonError(null);
                    } catch (e) {
                      setJsonError('Invalid JSON syntax');
                    }
                  }}
                  sx={{ 
                    flex: 1,
                    '& .MuiInputBase-root': { 
                      height: '100%',
                      alignItems: 'flex-start',
                      fontFamily: 'monospace',
                      fontSize: '13px'
                    },
                    '& .MuiInputBase-input': {
                      height: '100% !important',
                      overflow: 'auto !important'
                    }
                  }}
                  error={!!jsonError}
                />
              </Box>
            )}
          </Box>
          {reviewFormConfig?.showSourceDocument !== false && showOriginalDocs && (
            <Box sx={{ width: '50%', borderLeft: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                {inputDisplayConfig.panel_title}
              </Typography>
              <Box sx={{ flex: 1, p: 1 }}>
                <PDFViewer status={status} />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setJsonEditorOpen(false)}>
            {reviewFormConfig?.cancelButtonText || 'Cancel'}
          </Button>
          {stepControls.allowIntermediateEdit && sourceS3Uri && (
            <Button
              onClick={handleSaveReview}
              variant="outlined"
              disabled={saving || !!jsonError}
            >
              {saving ? 'Saving...' : saveSuccess ? 'Saved \u2713' : (reviewFormConfig?.saveButtonText ?? 'Save Progress')}
            </Button>
          )}
          <Button 
            onClick={handleSubmitToNextStep}
            variant="contained"
            color="primary"
            size="large"
            disabled={!!jsonError}
          >
            {reviewFormConfig?.submitButtonText || completionActions.continueButtonText || 'Continue to Next Step'}
          </Button>
        </DialogActions>
      </Dialog>
      

    </Box>
  );
}